import { Citadel } from "@msuite/citadel";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import { useMemo } from "react";
import { authClient } from "@/api/auth";
import { client } from "@/hono";

export function useCitadel() {
	/** Hooks */
	const session = authClient.useSession();

	/** Query */
	const { data, ...query } = useQuery({
		queryKey: ["citadel", guardString(session.data?.session.activeOrganizationId)],
		queryFn: async () => {
			const response = await client.me.permissions.$get();
			return await responseHandler(response, false);
		},
		staleTime: 60_000,
		refetchOnWindowFocus: true,
		enabled: !!session.data?.session.id,
	});

	/** Computed */
	// The query cache is JSON-persisted, so it must only ever hold plain data:
	// a persisted class instance rehydrates as a methodless object and crashes
	// the first permission check after a reload. The instance is rebuilt from
	// the cached options instead.
	const citadel = useMemo(() => (data ? new Citadel(data) : undefined), [data]);

	return {
		...query,
		citadel,
		// The same rules the ability was built from, each carrying the roles that
		// grant it and whether it is also set individually. `citadel` answers
		// whether the caller may do something; this answers why, which is what a
		// permission view shows and what the ability deliberately does not keep.
		permissions: data?.permissions,
		// Mirrors the server's guard on every role write (`Roles.canManageRoles`):
		// owner or bearer of the `admin` system role, decided in the database. This
		// is NOT the same question as `citadel.can("manage", "Permission")` — that
		// pair is an ordinary manifest entry the individual grid can hand to anyone,
		// and it opens the permission views without opening the role writes. Gate
		// role controls on this, or they 403 on submit.
		canManageRoles: data?.canManageRoles === true,
	};
}
